import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import ZeroFrictionOnboarding from './ZeroFrictionOnboarding';
import SoftSubscriptionAsk from './SoftSubscriptionAsk';
import { LogOut } from 'lucide-react';

/**
 * JourneyGate - Controls the user journey flow
 * 
 * ZERO-FRICTION FLOW:
 * 1. New user → Zero-friction onboarding (ONE emotional moment, no questions)
 * 2. Straight to app → Full access during 7-day trial
 * 3. Day 5+ OR low engagement → Soft subscription ask
 * 
 * NO:
 * - Feature tours
 * - Multi-question onboarding
 * - Immediate paywalls
 * - Permission walls
 */
const JourneyGate = ({ children }) => {
  const { user, logout } = useAuth();
  const [journeyStatus, setJourneyStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [showSubscriptionAsk, setShowSubscriptionAsk] = useState(false);
  const [showHardPaywall, setShowHardPaywall] = useState(false);  // NEW: Hard block
  const [userStats, setUserStats] = useState(null);

  useEffect(() => {
    fetchJourneyStatus();
  }, []);

  const fetchJourneyStatus = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/user/journey-status`,
        { credentials: 'include' }
      );
      
      if (response.ok) {
        const data = await response.json();
        setJourneyStatus(data);
        
        // Determine what to show - priority order:
        // 1. Hard paywall (trial used, no subscription)
        // 2. Onboarding
        // 3. Soft subscription ask
        if (data.must_subscribe) {
          // HARD BLOCK - user must pay to continue
          setShowHardPaywall(true);
        } else if (data.needs_onboarding) {
          setShowOnboarding(true);
        } else if (data.show_subscription_ask && !data.has_subscription) {
          // Fetch stats for subscription ask
          fetchUserStats();
          setShowSubscriptionAsk(true);
        }
      }
    } catch (error) {
      console.error('Journey status error:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUserStats = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/user/stats-for-subscription`,
        { credentials: 'include' }
      );
      if (response.ok) {
        const data = await response.json();
        setUserStats(data);
      }
    } catch (error) {
      console.error('User stats error:', error);
    }
  };

  const handleOnboardingComplete = () => {
    setShowOnboarding(false);
    // Track app open for analytics
    trackEvent('app_open', { day_of_usage: 1 });
    fetchJourneyStatus();
  };

  const handleSubscribe = async () => {
    // Redirect to payment
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/payments/checkout/subscription`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            plan_id: 'pro_monthly',
            origin_url: window.location.origin
          })
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        if (data.checkout_url) {
          window.location.href = data.checkout_url;
        }
      }
    } catch (error) {
      console.error('Subscription error:', error);
    }
  };

  const handleMaybeLater = async () => {
    try {
      await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/user/dismiss-subscription-ask`,
        {
          method: 'POST',
          credentials: 'include'
        }
      );
    } catch (error) {
      console.error('Dismiss error:', error);
    }
    setShowSubscriptionAsk(false);
  };

  const trackEvent = async (event, data = {}) => {
    try {
      await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/analytics/track`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ event, data })
        }
      );
    } catch (error) {
      console.error('Track event error:', error);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-teal-50/80 via-cyan-50/60 to-white flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 bg-gradient-to-br from-teal-500 to-cyan-500 rounded-2xl flex items-center justify-center mx-auto mb-4 animate-pulse">
            <span className="text-2xl">💜</span>
          </div>
          <p className="text-gray-500">Loading your journey...</p>
        </div>
      </div>
    );
  }

  // Show ZERO-FRICTION onboarding for new users
  if (showOnboarding) {
    return <ZeroFrictionOnboarding userName={user?.name} onComplete={handleOnboardingComplete} />;
  }

  // HARD BLOCK: User has used their trial and must subscribe to continue
  if (showHardPaywall) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-teal-50/80 via-cyan-50/60 to-white px-4 py-8">
        <div className="max-w-md mx-auto animate-fade-in-up">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="w-20 h-20 bg-gradient-to-br from-teal-500 to-cyan-500 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
              <span className="text-3xl">🔒</span>
            </div>
            <h1 className="text-2xl font-bold text-gray-800 mb-2">
              Your Free Trial Has Ended
            </h1>
            <p className="text-gray-500 text-sm">
              Subscribe to continue using LifeFlo and keep all your data
            </p>
          </div>

          {/* Subscription Card */}
          <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="font-semibold text-gray-800">Pro Monthly</h3>
                <p className="text-xs text-gray-500">Full access to all features</p>
              </div>
              <div className="text-right">
                <p className="text-2xl font-bold text-gray-800">$9.99</p>
                <p className="text-xs text-gray-400">/month</p>
              </div>
            </div>
            
            <div className="space-y-2 mb-6">
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <span className="text-green-500">✓</span>
                <span>Unlimited AI assistant conversations</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <span className="text-green-500">✓</span>
                <span>Task management & calendar</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <span className="text-green-500">✓</span>
                <span>Health & financial tracking</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <span className="text-green-500">✓</span>
                <span>Priority support</span>
              </div>
            </div>
            
            <button
              onClick={handleSubscribe}
              className="w-full py-3 bg-teal-600 hover:bg-teal-700 text-white rounded-xl font-medium transition-all"
            >
              Subscribe Now
            </button>
          </div>

          {/* Yearly Option */}
          <div className="bg-white/80 rounded-2xl border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="flex items-center gap-2">
                  <h3 className="font-semibold text-gray-800">Pro Yearly</h3>
                  <span className="px-2 py-0.5 bg-orange-100 text-orange-700 text-xs font-medium rounded-full">
                    Save 17%
                  </span>
                </div>
                <p className="text-xs text-gray-500">Best value</p>
              </div>
              <div className="text-right">
                <p className="text-2xl font-bold text-gray-800">$99</p>
                <p className="text-xs text-gray-400">/year</p>
              </div>
            </div>
            
            <button
              onClick={() => {
                fetch(`${process.env.REACT_APP_BACKEND_URL}/api/payments/checkout/subscription`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  credentials: 'include',
                  body: JSON.stringify({ plan_id: 'pro_annual', origin_url: window.location.origin })
                })
                .then(r => r.json())
                .then(data => { if (data.checkout_url) window.location.href = data.checkout_url; });
              }}
              className="w-full py-3 bg-gray-800 hover:bg-gray-900 text-white rounded-xl font-medium transition-all"
            >
              Subscribe Yearly
            </button>
          </div>

          <p className="text-xs text-gray-400 text-center mt-6">
            Cancel anytime. Your data is always yours.
          </p>

          {/* Logout Option */}
          <button
            onClick={logout}
            className="w-full mt-4 py-3 flex items-center justify-center gap-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-xl transition-all"
          >
            <LogOut size={16} />
            <span className="text-sm">Log out and use a different account</span>
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      {children}
      
      {/* Soft subscription ask (modal, not blocking) */}
      {showSubscriptionAsk && (
        <SoftSubscriptionAsk
          onSubscribe={handleSubscribe}
          onMaybeLater={handleMaybeLater}
          userStats={userStats}
        />
      )}
    </>
  );
};

export default JourneyGate;
